import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { FileReviewDocumentKind, FileReviewPanelPlacement } from './types';
import { readFileReviewPanelWidth, writeFileReviewPanelWidth } from './fileReviewUiPrefs';

const DEFAULT_WIDTH = 340;
const MIN_WIDTH = 320;
const MAX_WIDTH = 520;

function clampWidth(n: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n));
}

export function useFileReviewPanelWidth(
  workspaceRef: RefObject<HTMLElement | null>,
  placement: FileReviewPanelPlacement,
  documentType?: FileReviewDocumentKind
) {
  const persisted = readFileReviewPanelWidth(documentType);
  const [panelWidth, setPanelWidth] = useState(() => clampWidth(persisted ?? DEFAULT_WIDTH));
  const isDraggingRef = useRef(false);
  const latestWidthRef = useRef(panelWidth);

  useEffect(() => {
    latestWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!isDraggingRef.current) return;
      const rect = workspaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next =
        placement === 'left' ? e.clientX - rect.left : rect.right - e.clientX;
      setPanelWidth(clampWidth(next));
    }
    function onMouseUp() {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.classList.remove('is-resizing-sidebar');
      writeFileReviewPanelWidth(latestWidthRef.current, documentType);
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [documentType, placement, workspaceRef]);

  const startResize = useCallback((e: ReactMouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.classList.add('is-resizing-sidebar');
  }, []);

  const resetWidth = useCallback(() => {
    setPanelWidth(DEFAULT_WIDTH);
    writeFileReviewPanelWidth(DEFAULT_WIDTH, documentType);
  }, [documentType]);

  const panelStyle = {
    flex: `0 0 ${panelWidth}px`,
    width: panelWidth,
    minWidth: panelWidth,
    maxWidth: panelWidth
  } as const;

  return { panelWidth, panelStyle, startResize, resetWidth };
}
